import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ExternalLink, Filter, Gamepad2, Grid, List } from 'lucide-react';
import Header from './Header';
import SEO from './SEO';
import gamesData from '../data/reddit-games.json';

const RedditGamesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Extract unique categories
  const categories = useMemo(() => {
    const allTypes = new Set();
    gamesData.forEach(game => {
      if (game.types) {
        game.types.forEach(type => allTypes.add(type));
      }
    });
    return ['All', ...Array.from(allTypes).sort()];
  }, []);

  // Filter games
  const filteredGames = useMemo(() => {
    const baseGames = gamesData.filter(game => {
      const matchesSearch = (game.game_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (game.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || (game.types && game.types.includes(selectedCategory));
      
      return matchesSearch && matchesCategory;
    });

    // Add Subbzle if it matches filters (or always if no filters? Let's make it respect filters but be pinned if matches)
    // Actually, user asked to "add a very distinct game called Centumth".
    // I'll add it to the list manually if it's not in the JSON (it won't be as it's manual).
    // And I'll make sure it respects the "Word" category filter.
    
    const sponsoredGame = {
      game_name: "Centumth",
      description: "Guess the items ranked closest to #100 in today's category. Aim for the highest score!",
      types: ["Word"],
      subreddit_link: "https://www.centumth.com/",
      isSponsored: true
    };

    let allGames = [...baseGames];

    // Check if Centumth matches current filters
    const sponsoredMatchesSearch = (sponsoredGame.game_name).toLowerCase().includes(searchQuery.toLowerCase()) ||
                                 (sponsoredGame.description).toLowerCase().includes(searchQuery.toLowerCase());
    
    // Sponsored games show on ALL categories, but must match search
    if (sponsoredMatchesSearch) {
      // Prepend Centumth
      allGames = [sponsoredGame, ...allGames];
    }

    return allGames;
  }, [searchQuery, selectedCategory]);

  return (
    <div className="flex flex-col min-h-screen bg-[#f8f9fa] bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:24px_24px]">
      <SEO 
        title="Reddit Games Directory - PocketHints" 
        description="Discover the best games on Reddit. A curated directory of word games, puzzles, arcade games, and more played directly on subreddits."
        url={window.location.origin + '/reddit-games'}
      />

      <Header 
        date={new Date().toISOString()} 
        title="Reddit Games Directory" 
        gameName="Reddit Games" 
        heroTitle="The Ultimate Reddit Games Directory"
        introText="Explore a curated collection of games played right here on Reddit. From daily word puzzles to collaborative world-building."
      />

      <main className="flex-grow max-w-7xl mx-auto px-4 w-full pb-20">
        
        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <a 
            href="https://tally.so/r/oby5p5" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 py-3 bg-white text-gray-700 font-bold rounded-xl border border-gray-200 shadow-sm hover:border-brand-blue hover:text-brand-blue hover:shadow-md transition-all duration-200"
          >
            Submit a Game
          </a>
          <a 
            href="https://tally.so/r/rj6Jpp" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 py-3 bg-brand-blue text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-600 hover:scale-105 transition-all duration-200"
          >
            Become a Sponsor
          </a>
        </div>

        {/* Controls Section */}
        <div className="sticky top-24 z-30 bg-[#f8f9fa]/95 backdrop-blur-sm py-4 px-6 mb-8 shadow-sm rounded-2xl border border-white/50 mx-2 md:mx-0">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            
            {/* Search */}
            <div className="relative w-full md:w-96 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400 group-focus-within:text-brand-blue transition-colors" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all shadow-sm"
                placeholder="Search games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* View Toggle & Count */}
            <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
               <span className="text-sm font-bold text-gray-500">
                 {filteredGames.length} Games Found
               </span>
               
               <div className="flex bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
                 <button 
                   onClick={() => setViewMode('grid')}
                   className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-brand-blue text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                   aria-label="Grid View"
                 >
                   <Grid size={18} />
                 </button>
                 <button 
                   onClick={() => setViewMode('list')}
                   className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-brand-blue text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                   aria-label="List View"
                 >
                   <List size={18} />
                 </button>
               </div>
            </div>
          </div>

          {/* Categories */}
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-all border ${
                  selectedCategory === category
                    ? 'bg-brand-blue text-white border-brand-blue shadow-md transform scale-105'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-brand-blue/30 hover:bg-brand-blue/5'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Games Grid */}
        <AnimatePresence mode='wait'>
          <motion.div 
            key={viewMode + selectedCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
              : "flex flex-col gap-4"
            }
          >
            {filteredGames.map((game, index) => {
              const card = <GameCard key={`game-${index}`} game={game} viewMode={viewMode} />;
              
              // Insert an ad placeholder every 9 items (e.g., after index 8, 17, etc.)
              // We use col-span-full so it spans the entire width of the grid
              if (index > 0 && index % 9 === 0 && index !== filteredGames.length - 1) {
                return (
                  <React.Fragment key={`group-${index}`}>
                    <div className="content_hint col-span-full w-full flex justify-center items-center my-4 min-h-[250px] bg-gray-50/50 rounded-2xl border border-gray-100 border-dashed">
                      <span className="text-gray-300 text-sm font-medium uppercase tracking-widest">Advertisement</span>
                    </div>
                    {card}
                  </React.Fragment>
                );
              }
              return card;
            })}
          </motion.div>
        </AnimatePresence>

        {filteredGames.length === 0 && (
          <div className="text-center py-20">
            <Gamepad2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No games found</h3>
            <p className="text-gray-500">Try adjusting your search or filters.</p>
            <button 
              onClick={() => {setSearchQuery(''); setSelectedCategory('All');}}
              className="mt-6 px-6 py-2 bg-brand-blue text-white rounded-lg font-bold hover:bg-blue-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}

      </main>
    </div>
  );
};

const GameCard = ({ game, viewMode }) => {
  const isGrid = viewMode === 'grid';
  const isSponsored = game.isSponsored;

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group rounded-2xl overflow-hidden transition-all duration-300 ${
        isSponsored 
          ? 'bg-black border-2 border-gray-800 shadow-xl' 
          : 'bg-white border border-gray-100 hover:shadow-xl hover:border-brand-blue/20'
      } ${isGrid ? 'flex flex-col h-full' : 'flex flex-row items-center p-6 gap-6'}`}
    >
      <div className={`flex-grow ${isGrid ? 'p-6' : ''}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex flex-wrap gap-2">
            {isSponsored && (
              <span className="px-2 py-1 bg-white/10 text-white text-xs font-bold uppercase tracking-wider rounded-md border border-white/20">
                Sponsored
              </span>
            )}
            {game.types && game.types.map((type, i) => (
              <span key={i} className={`px-2 py-1 text-xs font-bold uppercase tracking-wider rounded-md ${
                isSponsored ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>
                {type}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 mb-3">
           {isSponsored ? (
             <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 border border-gray-800">
               <img src={process.env.PUBLIC_URL + '/centumth-logo.png'} alt="Centumth Logo" className="w-8 h-8 object-contain" />
             </div>
           ) : null}
           <h3 className={`text-xl font-black transition-colors ${
             isSponsored ? 'text-white' : 'text-gray-900 group-hover:text-brand-blue'
           }`}>
             {game.game_name}
           </h3>
        </div>
        
        <p className={`text-sm leading-relaxed line-clamp-3 ${
          isSponsored ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {game.description}
        </p>
      </div>

      <div className={`${isGrid ? 'p-6 pt-0 mt-auto' : 'flex-shrink-0 w-48'}`}>
        <div className={`${isGrid ? 'border-t pt-6' : ''} ${isSponsored ? 'border-gray-800' : 'border-gray-50'}`}>
          <a 
            href={game.subreddit_link} 
            target="_blank" 
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center gap-2 w-full px-4 py-3 font-bold rounded-xl transition-all duration-200 ${
              isSponsored 
                ? 'bg-white text-black hover:bg-gray-200' 
                : 'bg-brand-blue/5 text-brand-blue hover:bg-brand-blue hover:text-white'
            } ${!isGrid && 'h-full'}`}
          >
            <span>{isSponsored ? 'Play Now' : 'Play on Reddit'}</span>
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default RedditGamesPage;

